<script>
    import { onDestroy, tick, createEventDispatcher } from "svelte";
    import { api as slugApi } from "@/services/slug";
    import { route } from "@/stores/route";
    import { translate, buildClassStringFromTags } from "@/lib/utils";
    import LoadingDots from "@/components/LoadingDots.svelte";
    import { classes } from "@/stores/classes";
    import Popup from "@/components/Popup.svelte";
    import Contact from "../components/Contact.svelte";

    $: $route && getSlug();
    $: data = null;

    let destroyed = false;
    let loading = false;
    let path = null;
    let activeListeners = [];

    let popupEhrenamtliche = false;
    let popupFoerderer = false;
    let popupPartner = false;

    const dispatch = createEventDispatcher();

    const unregisterListeners = () => {
        activeListeners.forEach(({ element, type, listener }) => {
            element.removeEventListener(type, listener);
        });
        activeListeners = [];
    };

    const registerListener = (element, type, listener) => {
        element.addEventListener(type, listener);
        activeListeners.push({ element, type, listener });
    };

    onDestroy(() => {
        unregisterListeners();
        destroyed = true;
    });

    const loadComponent = async (layout) => {
        return (await import(`../pages/${layout}.svelte`)).default;
    };

    const getSlug = async () => {
        path = window.location.pathname.substr(1) || "startseite";

        if (destroyed === false && !loading) {
            unregisterListeners();
            loading = true;

            await slugApi
                .search(path)
                .then(async (response) => {
                    data = response.data || {};
                    data.component = await loadComponent(
                        data.meta.resource_class_name,
                    );
                    setMeta();
                    setTags();
                })
                .catch(async (response) => {
                    data = response.data || {};
                    data.component = await loadComponent("NotFound");
                })
                .finally(() => (loading = false));
        }
    };

    const setMeta = () => {
        document.title =
            translate(data?.data?.meta_title) ||
            translate(data?.data?.name) ||
            "";

        const metaDescription = document.querySelector(
            'meta[name="description"]',
        );

        if (metaDescription) {
            metaDescription.setAttribute(
                "content",
                translate(data?.data?.meta_description) || "",
            );
        } else {
            const newMetaDescription = document.createElement("meta");
            newMetaDescription.setAttribute("name", "description");
            newMetaDescription.setAttribute(
                "content",
                translate(data?.data?.meta_description) || "",
            );
            document.head.appendChild(newMetaDescription);
        }
    };

    const setTags = () => {
        classes.set(buildClassStringFromTags(data?.data?.tags));
    };

    const componentLoaded = async () => {
        await tick();

        document.querySelectorAll("[data-popup]").forEach((element) => {
            const listener = (event) => {
                const popupValue = event.currentTarget.dataset.popup;

                if (popupValue === "ehrenamtlich") {
                    popupEhrenamtliche = true;
                }

                if (popupValue === "foerderer") {
                    popupFoerderer = true;
                }

                if (popupValue === "partner") {
                    popupPartner = true;
                }
            };
            registerListener(element, "click", listener);
        });

        dispatch("loaded", true);
    };

    export function loadScript(src, element) {
        return new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = src;

            element.appendChild(script);

            script.addEventListener("load", () => resolve(script));
            script.addEventListener("error", () => reject(script));
        });
    }

    getSlug();
</script>

<main class={classes}>
    {#if data && !loading}
        <svelte:component
            this={data.component}
            on:loaded={componentLoaded}
            on:fixheader={(e) => dispatch("fixheader", e.detail)}
            {data}
        />
    {:else}
        <div class="h-screen w-full relative flex items-center justify-center">
            <LoadingDots />
        </div>
    {/if}

    <Popup
        open={popupEhrenamtliche}
        popupClass="lg:!w-1/2 max-h-[100%] bg-white text-black !p-0"
        on:close={() => (popupEhrenamtliche = false)}
    >
        <div class="p-4">
            <h2>
                {translate({
                    de: "Ehrenamtlich",
                    en: "Volunteer",
                })}
            </h2>
            <p>
                {translate({
                    de: "Bist Du jemand, der gerne selber aktiv und praktisch unterstützt? Dann bist Du bei uns herzlich willkommen! Schreib uns eine Nachricht und wir melden uns bei Dir!",
                    en: "Are you someone who enjoys getting actively involved and offering hands-on support? Then you’re very welcome to join us! Send us a message and we’ll get back to you!",
                })}
            </p>
            <Contact formID="Ehrenamtliche" />
        </div>
    </Popup>

    <Popup
        open={popupFoerderer}
        popupClass="lg:!w-1/2 max-h-[100%] bg-white text-black !p-0"
        on:close={() => (popupFoerderer = false)}
    >
        <div class="p-4">
            <h2>
                {translate({
                    de: "Förderer",
                    en: "Supporter",
                })}
            </h2>
            <p>
                {translate({
                    de: "Du möchtest HOME-Pate werden oder Dich mit einer Spende an einem unserer Projekte beteiligen? Dann freuen wir uns, von Dir zu hören. Schreib uns eine Nachricht und wir melden uns bei Dir!",
                    en: "Would you like to become a HOME sponsor or support one of our projects with a donation? Then we’d love to hear from you. Send us a message and we’ll get back to you!",
                })}
            </p>
            <Contact formID="Foerderer" />
        </div>
    </Popup>

    <Popup
        open={popupPartner}
        popupClass="lg:!w-1/2 max-h-[100%] bg-white text-black !p-0"
        on:close={() => (popupPartner = false)}
    >
        <div class="p-4">
            <h2>
                {translate({
                    de: "Partner",
                    en: "Partner",
                })}
            </h2>
            <p>
                {translate({
                    de: "Bist Du eine Fach- und Führungskraft aus einer sozialen Einrichtung, Behörde oder anderen NGO und arbeitest gerne interdisziplinär zusammen? Dann bist Du bei uns herzlich willkommen! Schreib uns eine Nachricht und wir melden uns bei Dir!",
                    en: "Are you a professional or manager from a social organization, public authority, or another NGO who enjoys working collaboratively across disciplines? Then you’re very welcome to join us! Send us a message and we’ll get back to you!",
                })}
            </p>
            <Contact formID="Partner" />
        </div>
    </Popup>
</main>
